import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isNumber from 'lodash/isNumber';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import OnboardingDaysButton from './OnboardingDaysButton';
import useIsMobile from '../../hooks/useIsMobile';
import OnboardingDatePicker from '../datePickers/OnboardingDatePicker';
import { getDayDifference, TODAY } from '../../constants/dates';
import dayjs from '../../config/dayjs';
import { TEXT_FIELD_ICON_CLASS_NAME } from '../../theme/constants/icon';
import { ONBOARDING_OUTLINED_INPUT_CLASS_NAME, FORM_OUTLINED_INPUT_CLASS_NAME } from '../../theme/constants/outlined-input';
import { ONBOARDING_INPUT_LABEL_CLASS_NAME } from '../../theme/constants/input-label';

const OTHER_INPUT_SELECTED = -1;

const otherInputStyles = {
  height: '100%',
  minWidth: '85px',
  p: 0,
  pl: 1,
  '& .MuiOutlinedInput-input': {
    height: '90%',
  },
};

const SelectDaysButtons = ({
  daysButtons,
  selectedDays,
  setSelectedDays,
  setDaysToPay,
  formatLabelDays,
  daysToPay,
  maxDaysToPay,
  minDaysToPay,
  showButtonSubtitle,
  buttonProps,
  rowProps,
  showDatePicker,
  datePickerProps,
  useSmallDaysButtons,
  unselectButtons,
}) => {
  const isMobile = useIsMobile();
  const iconButtonRef = useRef(null);

  useEffect(() => {
    if (unselectButtons) {
      setSelectedDays(null);
      setDaysToPay(null);
    }
  }, [unselectButtons]);

  const [averagePaymentDaysSelected, setAveragePaymentDaysSelected] = useState(false);
  const [averagePaymentDays, setAveragePaymentDays] = useState(null);

  const DateButtonComponent = useCallback(({
    setOpen,
    inputRef,
    error,
    inputProps,
    InputProps,
    ...buttonParams }) => (
      <IconButton
        ref={iconButtonRef}
        edge="start"
        size="small"
        sx={{ m: 0, width: 24, height: 24 }}
        id="other-input-date-picker-button"
        {...buttonParams}
        onClick={() => {
          setOpen((prev) => !prev);
        }}
      >
        <EventOutlinedIcon className={TEXT_FIELD_ICON_CLASS_NAME} />
      </IconButton>
  ), []);

  const onDaysButtonClick = (days) => {
    setAveragePaymentDaysSelected(false);
    setSelectedDays(days);
    setDaysToPay(days);
  };

  const setOtherDays = (event) => {
    const { target: { value } } = event;
    const cleanValue = Number(value.replace(/[^0-9]/g, ''));
    if (isNumber(cleanValue)) {
      const clampedValue = Math.min(Math.max(cleanValue, minDaysToPay), maxDaysToPay);
      setDaysToPay(clampedValue);
    }
  };
  const outlinedInputCursor = showDatePicker ? 'pointer' : 'text';

  const onRecommendedPaymentTerm = (newValue, row) => {
    if (datePickerProps?.onRecommendedPaymentTerm) {
      setAveragePaymentDaysSelected(true);
      setAveragePaymentDays(datePickerProps?.value);
      return datePickerProps.onRecommendedPaymentTerm(newValue, row);
    }
    return () => { };
  };

  const otherInputValue = useMemo(() => {
    if (averagePaymentDaysSelected && selectedDays
      && averagePaymentDays === datePickerProps?.value) {
      return 'P.S.';
    }
    if (showDatePicker && selectedDays === OTHER_INPUT_SELECTED) {
      const days = getDayDifference(datePickerProps?.value, TODAY);
      return days > minDaysToPay ? days : '';
    }
    if (selectedDays === OTHER_INPUT_SELECTED) {
      return formatLabelDays(daysToPay);
    }
    return '';
  }, [selectedDays,
    daysToPay,
    datePickerProps?.value,
    TODAY,
    averagePaymentDaysSelected,
    averagePaymentDays,
    showDatePicker,
  ]);

  const onOtherInputClick = () => {
    setDaysToPay(
      isNumber(otherInputValue) && selectedDays === OTHER_INPUT_SELECTED
        ? otherInputValue
        : 0,
    );
    setSelectedDays(OTHER_INPUT_SELECTED);

    if (showDatePicker && iconButtonRef.current) {
      iconButtonRef.current.click();
    }
  };

  return (
    <>
      {daysButtons.map(({ days, label, id }) => (
        <Grid item xs={useSmallDaysButtons ? 2.5 : 3} md={useSmallDaysButtons ? 2.5 : 3} key={days} id={`select-days-buttons-${id}`}>
          <Box {...rowProps}>
            <OnboardingDaysButton
              label={label}
              variant="onboardingOutlined"
              color="primary"
              id={id}
              onClick={() => onDaysButtonClick(days)}
              className={selectedDays === days ? 'Mui-selected' : ''}
              showButtonSubtitle={showButtonSubtitle}
              labelVariant={isMobile ? 'small_2' : 'medium_2'}
              subtitleVariant="small_1"
              buttonProps={{ ...buttonProps }}
            />
          </Box>
        </Grid>
      ))}
      <Grid item xs={useSmallDaysButtons ? 4.5 : 3} md={useSmallDaysButtons ? 4.5 : 3}>
        <Box {...rowProps}>
          <FormControl fullWidth sx={{ height: '100%', backgroundColor: 'white', borderRadius: 2 }}>
            <InputLabel
              htmlFor="other-input"
              shrink
              className={`${ONBOARDING_INPUT_LABEL_CLASS_NAME} ${selectedDays === OTHER_INPUT_SELECTED ? 'Mui-selected' : ''}`}
            >
              Otro
            </InputLabel>
            <OutlinedInput
              id="other-input"
              fullWidth
              label="Otro"
              placeholder="Ej: 45"
              sx={otherInputStyles}
              className={`${ONBOARDING_OUTLINED_INPUT_CLASS_NAME} ${FORM_OUTLINED_INPUT_CLASS_NAME} ${selectedDays === OTHER_INPUT_SELECTED && otherInputValue ? 'Mui-selected' : ''}`}
              onClick={onOtherInputClick}
              onChange={setOtherDays}
              value={otherInputValue}
              readOnly={showDatePicker}
              inputProps={{
                sx: {
                  cursor: outlinedInputCursor,
                },
              }}
              startAdornment={
                showDatePicker ? (
                  <OnboardingDatePicker
                    value={datePickerProps?.value}
                    onChange={datePickerProps?.onChange || (() => { })}
                    onRecommendedPaymentTerm={
                      onRecommendedPaymentTerm
                    }
                    onChangeAll={datePickerProps?.onChangeAll}
                    debtorAveragePaymentDays={datePickerProps?.debtorAveragePaymentDays}
                    minDate={datePickerProps?.minDate}
                    maxDate={datePickerProps?.maxDate}
                    disabled={false}
                    shouldDisableWeekend={datePickerProps?.shouldDisableWeekend}
                    ButtonComponent={DateButtonComponent}
                    stackStyles={{ height: '100%', justifyContent: 'center' }}
                  />
                ) : null
              }
            />
          </FormControl>
        </Box>
      </Grid>
    </>
  );
};

SelectDaysButtons.propTypes = {
  daysButtons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      days: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedDays: PropTypes.number,
  setSelectedDays: PropTypes.func.isRequired,
  setDaysToPay: PropTypes.func.isRequired,
  formatLabelDays: PropTypes.func.isRequired,
  daysToPay: PropTypes.number,
  maxDaysToPay: PropTypes.number,
  minDaysToPay: PropTypes.number,
  showButtonSubtitle: PropTypes.bool,
  rowProps: PropTypes.shape({}),
  buttonProps: PropTypes.shape({}),
  showDatePicker: PropTypes.bool,
  datePickerProps: PropTypes.shape({
    value: PropTypes.instanceOf(dayjs),
    maxDate: PropTypes.instanceOf(dayjs),
    minDate: PropTypes.instanceOf(dayjs),
    debtorAveragePaymentDays: PropTypes.number,
    onChange: PropTypes.func,
    onChangeAll: PropTypes.func,
    shouldDisableWeekend: PropTypes.func,
    onRecommendedPaymentTerm: PropTypes.func,
  }),
  useSmallDaysButtons: PropTypes.bool,
  unselectButtons: PropTypes.bool,
};

SelectDaysButtons.defaultProps = {
  selectedDays: null,
  daysToPay: null,
  maxDaysToPay: 365,
  minDaysToPay: 0,
  showButtonSubtitle: false,
  buttonProps: {},
  rowProps: { sx: { height: { xs: '32px', md: '44px' } } },
  showDatePicker: false,
  datePickerProps: {},
  useSmallDaysButtons: false,
  unselectButtons: false,
};

export default SelectDaysButtons;
